import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Select, Statistic, Spin, message } from 'antd';
import {
    UserAddOutlined,
    FileTextOutlined,
    DollarOutlined,
    LineChartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import axiosInstance from '../../utils/axiosConfig';
import dayjs from 'dayjs';

const { Option } = Select;

const SERIES = [
    { key: 'registrations', label: 'Đăng ký tài khoản', color: '#4361ee', icon: <UserAddOutlined /> },
    { key: 'profiles',      label: 'Hoàn thành hồ sơ',  color: '#2dc653', icon: <FileTextOutlined /> },
    { key: 'payments',      label: 'Nộp lệ phí',         color: '#f77f00', icon: <DollarOutlined /> },
];

const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `Tháng ${i + 1}` }));

const currentYear = dayjs().year();
const years = Array.from({ length: currentYear - 2019 }, (_, i) => ({
    value: 2020 + i,
    label: String(2020 + i),
}));

const RegistrationStatistics = () => {
    const [chartData, setChartData]     = useState([]);
    const [totals, setTotals]           = useState({ registrations: 0, profiles: 0, payments: 0 });
    const [monthName, setMonthName]     = useState('');
    const [loading, setLoading]         = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
    const [selectedYear, setSelectedYear]   = useState(currentYear);
    const navigate = useNavigate();

    const fetchData = useCallback(async (month, year) => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/v1/admin/registration-statistics-by-month', {
                params: { month, year },
            });
            if (res.data.success) {
                setChartData(res.data.data.registrationByDay);
                setTotals(res.data.data.totals);
                setMonthName(res.data.data.monthName);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
                navigate('/login');
            } else {
                message.error('Có lỗi xảy ra khi lấy dữ liệu thống kê');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchData(selectedMonth, selectedYear);
    }, []);

    const handleMonthChange = (v) => {
        setSelectedMonth(v);
        fetchData(v, selectedYear);
    };

    const handleYearChange = (v) => {
        setSelectedYear(v);
        fetchData(selectedMonth, v);
    };

    const tooltipFormatter = (value, name) => {
        const series = SERIES.find(s => s.label === name);
        return [`${value} thí sinh`, series?.label ?? name];
    };

    return (
        <Card
            title={
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LineChartOutlined />
                    Thống kê hoạt động theo ngày
                    {monthName && (
                        <span style={{ fontWeight: 400, color: '#888', fontSize: 13 }}>
                            — {monthName}
                        </span>
                    )}
                </span>
            }
            extra={
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span>Tháng:</span>
                    <Select value={selectedMonth} onChange={handleMonthChange} style={{ width: 110 }}>
                        {months.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
                    </Select>
                    <span>Năm:</span>
                    <Select value={selectedYear} onChange={handleYearChange} style={{ width: 90 }}>
                        {years.map(y => <Option key={y.value} value={y.value}>{y.label}</Option>)}
                    </Select>
                </span>
            }
        >
            {/* Stat cards */}
            <Row gutter={16} style={{ marginBottom: 20 }}>
                {SERIES.map(s => (
                    <Col span={8} key={s.key}>
                        <Card size="small" style={{ borderTop: `3px solid ${s.color}` }}>
                            <Statistic
                                title={s.label}
                                value={totals[s.key]}
                                prefix={React.cloneElement(s.icon, { style: { color: s.color } })}
                                suffix="người"
                                valueStyle={{ color: s.color, fontSize: 22 }}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Chart */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 50 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div style={{ width: '100%', height: 380 }}>
                    <ResponsiveContainer>
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="day"
                                label={{ value: 'Ngày trong tháng', position: 'insideBottom', offset: -12 }}
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis
                                allowDecimals={false}
                                label={{ value: 'Số lượng', angle: -90, position: 'insideLeft', offset: 10 }}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip formatter={tooltipFormatter} />
                            <Legend verticalAlign="top" height={36} />
                            {SERIES.map(s => (
                                <Line
                                    key={s.key}
                                    type="monotone"
                                    dataKey={s.key}
                                    name={s.label}
                                    stroke={s.color}
                                    strokeWidth={2}
                                    dot={{ fill: s.color, r: 3 }}
                                    activeDot={{ r: 5 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </Card>
    );
};

export default RegistrationStatistics;
